import { Component } from '@theme/component';
import { ResizeNotifier } from '@theme/critical';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;
const DRAG_START_THRESHOLD_PX = 8;
const USER_SELECTION_SETTLE_TIMEOUT_MS = 700;

const nextAnimationFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
const timeout = (duration) => new Promise((resolve) => setTimeout(resolve, duration));

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  /**
   * Nested card galleries are intentionally non-interactive in carousel layouts,
   * but still need programmatic alignment when hover-switching images in Safari.
   * @type {boolean}
   */
  get #allowsDisabledProgrammaticAlignment() {
    return this.isNested && this.closest('.card-gallery') != null;
  }

  /** @type {IntersectionObserver | null} */
  #viewportObserver = null;

  async connectedCallback() {
    super.connectedCallback();

    // Eager hydration paths: Theme Editor needs immediate init, nested
    // slideshows are wired up by their parent's logic, browsers without
    // IntersectionObserver fall back to immediate init.
    if (window.Shopify?.designMode || this.isNested || typeof IntersectionObserver === 'undefined') {
      return this.#hydrate();
    }

    // All other slideshows defer hydration until they enter (or come within
    // 300px of) the viewport. The IntersectionObserver's first callback fires
    // very quickly (next render task) for visible elements, so above-the-fold
    // slideshows hydrate within ~1 frame — imperceptible. Off-screen
    // slideshows wait until scrolled near. This avoids the forced-reflow
    // burst when many components connect simultaneously (e.g. Show More
    // pagination renders 10+ slideshows in one batch).
    this.#deferHydration();
  }

  #deferHydration() {
    let woken = false;
    const wakeUp = () => {
      if (woken) return;
      woken = true;
      this.#viewportObserver?.disconnect();
      this.#viewportObserver = null;
      this.removeEventListener('pointerdown', wakeUp);
      this.removeEventListener('mouseenter', wakeUp);
      if (this.isConnected) this.#hydrate();
    };

    this.#viewportObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) wakeUp();
      },
      { rootMargin: '300px 0px' }
    );
    this.#viewportObserver.observe(this);

    // Fallback: user might tap a slideshow control before it scrolls into view.
    this.addEventListener('pointerdown', wakeUp, { once: true });
    this.addEventListener('mouseenter', wakeUp, { once: true });
  }

  async #hydrate() {
    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    // Prevent browser scroll restoration from affecting initial position
    // Reset synchronously before any async operations
    const { scroller } = this.refs;
    if (scroller.scrollLeft !== 0) {
      scroller.scrollLeft = 0;
    }

    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('pointerdown', this.#handlePointerDown);
    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM. Defer heavy layout work to requestIdleCallback so
    // script evaluation completes faster (fixes Safari 3.49s "Script Evaluated" block).
    scheduler.schedule(() => {
      const { scroller } = this.refs;
      if (scroller.scrollLeft !== 0) scroller.scrollLeft = 0;

      this.#resizeObserver = new ResizeNotifier(async () => {
        if (viewTransition.current) await viewTransition.current;

        if ((this.#visibleSlidesAmount ?? 0) > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }

        if (this.#allowsDisabledProgrammaticAlignment) return;
        this.#scheduleLayoutStabilizedAlignment();
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
      this.#updateControlsVisibility();

      const runHeavyInit = () => {
        if (!this.isConnected) return;

        let visibleSlidesAmount = 0;
        const initialSlideId = this.initialSlide?.getAttribute('slide-id');
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (scroller.scrollLeft !== 0) scroller.scrollLeft = 0;
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
        this.#visibleSlidesAmount = visibleSlidesAmount;

        if (this.#allowsDisabledProgrammaticAlignment) {
          scroller.scrollLeft = 0;
          this.setAttribute('initialized', '');
        } else {
          this.#scheduleLayoutStabilizedAlignment({ force: true, ensureInitialized: true });
        }

        window.addEventListener(
          'load',
          () => {
            if (this.#allowsDisabledProgrammaticAlignment) return;
            this.#scheduleLayoutStabilizedAlignment({ force: true });
          },
          { once: true }
        );
      };

      if (this.#allowsDisabledProgrammaticAlignment) {
        runHeavyInit();
      } else {
        const idle = typeof window.requestIdleCallback === 'function';
        idle ? window.requestIdleCallback(runHeavyInit, { timeout: 2000 }) : setTimeout(runHeavyInit, 100);
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { scroller } = this.refs;

    // Tear down the viewport-gated hydration observer if hydration never fired.
    if (this.#viewportObserver) {
      this.#viewportObserver.disconnect();
      this.#viewportObserver = null;
    }

    scroller.removeEventListener('pointerdown', this.#handlePointerDown);
    scroller.removeEventListener('mousedown', this.#handleMouseDown);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#scroll?.destroy();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    this.removeAttribute('initialized');
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    if (this.#mobileStaticGalleryActive) {
      event?.preventDefault();
      this.#resetMobileStaticGallery();
      return;
    }

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;

    // Guard if invalid
    if (requestedIndex === undefined || isNaN(requestedIndex) || requestedIndex === current) return;

    const { slides } = this;

    if (!slides?.length) return;
    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    const shouldSerializeUserSelection = event != null && !instant;
    let userSelectionRunId = null;
    if (shouldSerializeUserSelection) {
      if (this.#userSelectionInProgress || this.hasAttribute('transitioning')) {
        event.preventDefault();
        return;
      }

      userSelectionRunId = this.#startUserSelectionLock();
    }

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    const usesLoopReorder = !instant && !isAdjacentSlide && visibleSlides.length === 1;
    if (usesLoopReorder) {
      this.#disabled = true;
      await this.#waitForScrollSettled(); // ensure we're not mid-scroll

      const targetSlide = slides[index];
      const currentSlide = slides[current];
      if (!targetSlide || !currentSlide) {
        this.#disabled = false;
        if (userSelectionRunId != null) this.#releaseUserSelectionLock(userSelectionRunId);
        return;
      }

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#waitForScrollSettled();

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
        await nextAnimationFrame();

        this.#disabled = false;
        if (userSelectionRunId != null) this.#releaseUserSelectionLock(userSelectionRunId, { align: true });
      });
    }

    const slide = slides[index];
    if (!slide) {
      if (userSelectionRunId != null) this.#releaseUserSelectionLock(userSelectionRunId);
      return;
    }

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');
    this.#scroll.to(slide, { instant });
    this.current = this.slides?.indexOf(slide) || 0;

    // Safari can leave card galleries half-shifted after instant hover-driven slide
    // selection (Scroller.to() rounds to integer offsetLeft while slides sit at
    // fractional offsets). Force a precise post-select alignment for all card
    // galleries, not just nested ones - grid cards misalign the same way.
    if (instant && this.closest('.card-gallery') != null) {
      this.#alignToCurrentSlide({ force: true });
    }

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    if (userSelectionRunId != null && !usesLoopReorder) {
      this.#releaseUserSelectionLock(userSelectionRunId, { align: this.#allowsDisabledProgrammaticAlignment });
    }

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    return this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    return this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return getVisibleElements(this.refs.scroller, this.slides, SLIDE_VISIBLITY_THRESHOLD, 'x');
  }

  get previousIndex() {
    const { current } = this;
    const modifier = this.slideStep ?? (this.visibleSlides.length > 1 ? this.visibleSlides.length : 1);

    return current - modifier;
  }

  get nextIndex() {
    const { current } = this;
    const modifier = this.slideStep ?? (this.visibleSlides.length > 1 ? this.visibleSlides.length : 1);

    return current + modifier;
  }

  get slideStep() {
    const value = Number.parseInt(this.getAttribute('slide-step') ?? '', 10);

    return Number.isFinite(value) && value > 0 ? value : null;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * Indicates whether a user-initiated smooth selection is waiting for scroll/layout to settle.
   * @type {boolean}
   */
  #userSelectionInProgress = false;

  /**
   * Monotonic token used to ignore stale async transition completions.
   * @type {number}
   */
  #userSelectionRunId = 0;

  #startUserSelectionLock() {
    this.#userSelectionInProgress = true;
    this.#userSelectionRunId += 1;

    return this.#userSelectionRunId;
  }

  /**
   * Releases a user selection lock after the active scroll operation and one frame.
   * @param {number} runId
   * @param {{ align?: boolean }} [options]
   */
  async #releaseUserSelectionLock(runId, options = {}) {
    await this.#waitForScrollSettled();
    await nextAnimationFrame();

    if (runId !== this.#userSelectionRunId) return;

    if (options.align) this.#alignToCurrentSlide({ force: true });
    this.#userSelectionInProgress = false;
  }

  #waitForScrollSettled() {
    return Promise.race([this.#scroll.finished, timeout(USER_SELECTION_SETTLE_TIMEOUT_MS)]);
  }

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /** @type {number} */
  #visibleSlidesAmount = 0;

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    if (this.#mobileStaticGalleryActive) {
      this.#resetMobileStaticGallery();
      return;
    }

    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Tracks the pointer type of the most recent pointerdown event.
   * Used to skip mouse drag handling for touch interactions.
   * @type {string}
   */
  #lastPointerType = 'mouse';

  /**
   * Handles the 'pointerdown' event to track pointer type.
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handlePointerDown = (event) => {
    this.#lastPointerType = event.pointerType;
  };

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.#mobileStaticGalleryActive) return;
    if (this.disabled || this.#dragging) return;

    // Skip mouse drag handling for touch interactions - let native scroll-snap handle it
    if (this.#lastPointerType === 'touch') return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let dragStarted = false;
    let distanceTravelled = 0;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!dragStarted) {
        if (Math.abs(initialDelta) < DRAG_START_THRESHOLD_PX) return;

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        dragStarted = true;
        this.#dragging = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the pointer movement is large enough to be a drag.
        document.addEventListener('click', preventDefault, { once: true, signal });

        this.pause();
        this.setAttribute('dragging', '');
        this.#scroll.snap = false;
      }

      // Stop the event from bubbling up to parent slideshow components
      event.preventDefault();
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();

      if (!dragStarted) return;

      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const targetIndex = next + modifier;

      this.removeAttribute('dragging');
      if (this.hasPointerCapture(event.pointerId)) {
        this.releasePointerCapture(event.pointerId);
      }

      if (targetIndex !== current) {
        // Use select() which handles infinite loop wrapping and smooth animation
        await this.select(targetIndex);
      } else {
        // Snap back to current slide (no effective movement)
        const currentSlide = slides[current];
        if (currentSlide) this.#scroll.to(currentSlide);
      }

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  #handleBreakpointChange = () => {
    if (this.#mobileStaticGalleryActive) {
      this.#resetMobileStaticGallery();
    }
  };

  get #mobileStaticGalleryActive() {
    return !mediaQueryLarge.matches && this.closest('.card-gallery[data-mobile-static-gallery]') != null;
  }

  #resetMobileStaticGallery() {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    if (scroller.scrollLeft !== 0) scroller.scrollLeft = 0;
    if (this.current !== 0) this.current = 0;

    const slides = this.slides ?? [];
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== 0}`);
    });
  }

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #layoutAlignRunId = 0;

  #scheduleLayoutStabilizedAlignment = ({ force = false, ensureInitialized = false } = {}) => {
    if (!this.isConnected) return;
    if (this.#disabled) return;
    if (this.disabled && !this.#allowsDisabledProgrammaticAlignment) return;

    const runId = ++this.#layoutAlignRunId;

    const { scroller } = this.refs;
    const slides = this.slides;
    if (!(scroller instanceof HTMLElement)) return;
    if (!slides?.length) return;

    // Temporarily disable snap while the layout is still changing.
    scroller.style.setProperty('scroll-snap-type', 'none');

    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome|Chromium|Edg/.test(navigator.userAgent);
    const maxFrames = isSafari ? 8 : 24;
    const stableFramesRequired = 3;
    let stableFrames = 0;
    let frames = 0;
    let lastSignature = '';

    const measureSignature = () => {
      const index = Number.isFinite(this.current) ? this.current : 0;
      const currentSlide = slides[Math.min(Math.max(index, 0), slides.length - 1)] ?? slides[0];
      if (!currentSlide) return '';

      const scrollerRect = scroller.getBoundingClientRect();
      const slideRect = currentSlide.getBoundingClientRect();

      // Rounded signature to ignore sub-pixel jitter while still detecting real layout changes.
      return [
        Math.round(scrollerRect.width),
        Math.round(scrollerRect.left),
        Math.round(scroller.scrollWidth),
        Math.round(scroller.clientWidth),
        Math.round(slideRect.width),
        Math.round(slideRect.left - scrollerRect.left),
      ].join('|');
    };

    const step = () => {
      if (!this.isConnected || runId !== this.#layoutAlignRunId) return;
      if (!force && (this.#dragging || this.hasAttribute('transitioning'))) return;

      frames += 1;

      // Read first to avoid layout thrashing (write triggers reflow before next read).
      const signature = measureSignature();
      const needsScrollReset =
        this.initialSlideIndex === 0 && this.current === 0 && scroller.scrollLeft !== 0;
      if (needsScrollReset) scroller.scrollLeft = 0;
      if (signature && signature === lastSignature) stableFrames += 1;
      else stableFrames = 0;
      lastSignature = signature;

      if (stableFrames >= stableFramesRequired || frames >= maxFrames) {
        this.#alignToCurrentSlide({ force });
        scroller.style.removeProperty('scroll-snap-type');
        if (ensureInitialized && !this.hasAttribute('initialized')) this.setAttribute('initialized', '');
        return;
      }

      requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  #alignToCurrentSlide = ({ force = false } = {}) => {
    if (!this.isConnected) return;
    if (this.#disabled) return;
    if (this.disabled && !this.#allowsDisabledProgrammaticAlignment) return;
    if (!force && (this.#dragging || this.hasAttribute('transitioning'))) return;

    const { scroller } = this.refs;
    const slides = this.slides;
    if (!(scroller instanceof HTMLElement)) return;
    if (!slides?.length) return;

    const currentIndex = Number.isFinite(this.current) ? this.current : 0;
    const currentSlide = slides[Math.min(Math.max(currentIndex, 0), slides.length - 1)] ?? slides[0];
    if (!currentSlide) return;

    // For the common case (initial slide 0), prefer hard 0 to avoid any snap/padding math.
    if (currentIndex === 0 && this.initialSlideIndex === 0) {
      if (scroller.scrollLeft !== 0) scroller.scrollLeft = 0;
      return;
    }

    const computedStyle = getComputedStyle(scroller);
    const paddingStart = parseFloat(computedStyle.paddingInlineStart || computedStyle.paddingLeft || '0');

    const scrollerRect = scroller.getBoundingClientRect();
    const slideRect = currentSlide.getBoundingClientRect();

    const targetLeft = scroller.scrollLeft + (slideRect.left - scrollerRect.left) - paddingStart;
    // Correct even sub-pixel misalignment: slides sit at fractional offsets while
    // Scroller.to() targets integer offsetLeft, leaving a ~1px sliver of the
    // neighbouring slide visible at the edge (most noticeable in Safari).
    if (Math.abs(targetLeft - scroller.scrollLeft) < 0.05) return;
    scroller.scrollLeft = targetLeft;
  };

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
